(function() {
    'use strict';

    angular
        .module('wamasysApp')
        .factory('ManualHelpContextService', ManualHelpContextService);

    ManualHelpContextService.$inject = ['$state', '$translate', 'AssistantPanelService', 'ManualHelp'];

    function ManualHelpContextService($state, $translate, AssistantPanelService, ManualHelp) {
        var state = {
            documents: [],
            currentDocument: null,
            activeDocumentKey: null,
            activeSectionId: null,
            documentResults: [],
            selectedResultDocKeys: [],
            selectedResultChunkIds: [],
            canSearch: false,
            canUseLlm: false,
            llmModeEnabled: false,
            hasSearchResults: false
        };

        return {
            state: state,
            setCapabilities: setCapabilities,
            setDocuments: setDocuments,
            setCurrentDocument: setCurrentDocument,
            setActiveLocation: setActiveLocation,
            clearSearchSelection: clearSearchSelection,
            openAssistant: openAssistant,
            navigateToResult: navigateToResult,
            enableLlmMode: enableLlmMode,
            syncAssistantContext: syncAssistantContext
        };

        function setCapabilities(canSearch, canUseLlm) {
            state.canSearch = !!canSearch;
            state.canUseLlm = !!canUseLlm;
            if (!state.canUseLlm) {
                state.llmModeEnabled = false;
            }
            syncAssistantContext();
        }

        function setDocuments(documents) {
            state.documents = angular.copy(documents || []);
        }

        function setCurrentDocument(document) {
            state.currentDocument = angular.copy(document || null);
            if (document && document.key) {
                state.activeDocumentKey = document.key;
            }
            syncAssistantContext();
        }

        function setActiveLocation(documentKey, sectionId) {
            if (documentKey) {
                state.activeDocumentKey = documentKey;
            }
            state.activeSectionId = sectionId || null;
            syncAssistantContext();
        }

        function clearSearchSelection() {
            state.selectedResultDocKeys = [];
            state.selectedResultChunkIds = [];
            state.hasSearchResults = false;
        }

        function openAssistant() {
            AssistantPanelService.open(buildAssistantContext());
        }

        function enableLlmMode() {
            if (!state.canUseLlm || !state.hasSearchResults) {
                return;
            }
            state.llmModeEnabled = true;
            syncAssistantContext();
        }

        function syncAssistantContext() {
            if (AssistantPanelService.state.isOpen
                && AssistantPanelService.state.context
                && AssistantPanelService.state.context.contextType === 'MANUAL_HELP') {
                AssistantPanelService.open(buildAssistantContext());
            }
        }

        function navigateToResult(result) {
            if (!result) {
                return;
            }
            state.selectedResultChunkIds = result.chunkId ? [result.chunkId] : [];
            state.selectedResultDocKeys = result.docKey ? [result.docKey] : [];
            setActiveLocation(result.docKey || state.activeDocumentKey, result.sectionAnchor || null);
            $state.go('manual-help', {
                doc: result.docKey || state.activeDocumentKey,
                section: result.sectionAnchor || null
            });
        }

        function buildAssistantContext() {
            return {
                contextType: 'MANUAL_HELP',
                entityId: 'manual-help',
                title: $translate.instant('global.assistant.manualTitle'),
                subtitle: resolveSubtitle(),
                promptPlaceholder: $translate.instant('global.assistant.manualPromptPlaceholder'),
                supportsActionProposal: false,
                supportsDataScopes: false,
                supportsAssistantFlowModes: false,
                defaultAssistantFlowMode: 'LEGACY',
                defaultUsageExpanded: state.llmModeEnabled,
                tokenWarningMessage: state.llmModeEnabled ? $translate.instant('global.assistant.manualTokenWarning') : null,
                canUseLlm: state.canUseLlm,
                llmModeEnabled: state.llmModeEnabled,
                hasSearchResults: state.hasSearchResults,
                showLlmUpgrade: !state.llmModeEnabled && state.hasSearchResults,
                llmUpgradeLockedMessage: !state.canUseLlm && state.hasSearchResults
                    ? $translate.instant('global.assistant.manualLlmLocked')
                    : null,
                llmUpgradeMessage: state.canUseLlm && state.hasSearchResults && !state.llmModeEnabled
                    ? $translate.instant('global.assistant.manualLlmUpgrade')
                    : null,
                enableLlmMode: enableLlmMode,
                adapter: {
                    getUsage: state.llmModeEnabled ? function(success, error) {
                        ManualHelp.getAssistantUsage(success, error);
                    } : null,
                    chat: function(payload, success, error) {
                        ManualHelp.chatAssistant(enrichPayload(payload), success, error);
                    },
                    extendChatPayload: function(payload) {
                        return enrichPayload(payload);
                    },
                    applyAssistantResponse: function(result, message) {
                        var documentResults = angular.copy((result && result.documentResults) || []);
                        message.documentResults = documentResults;
                        state.documentResults = documentResults;
                        state.selectedResultChunkIds = extractChunkIds(documentResults);
                        state.selectedResultDocKeys = extractDocKeys(documentResults);
                        state.hasSearchResults = documentResults.length > 0;
                    },
                    openDocumentResult: function(result) {
                        navigateToResult(result);
                    },
                    buildContextSignatureFragment: function() {
                        return [
                            state.activeDocumentKey || '',
                            state.activeSectionId || '',
                            state.llmModeEnabled ? 'llm' : 'search',
                            (state.selectedResultDocKeys || []).join(','),
                            (state.selectedResultChunkIds || []).join(',')
                        ].join('|');
                    },
                    buildSentContextInfo: function() {
                        return state.currentDocument && state.currentDocument.title ? state.currentDocument.title : '';
                    }
                }
            };
        }

        function enrichPayload(payload) {
            return angular.extend({}, payload || {}, {
                activeDocumentKey: state.activeDocumentKey,
                manualHelpLlmEnabled: state.llmModeEnabled,
                selectedResultDocKeys: angular.copy(state.selectedResultDocKeys || []),
                selectedResultChunkIds: angular.copy(state.selectedResultChunkIds || [])
            });
        }

        function resolveSubtitle() {
            return state.currentDocument && state.currentDocument.title
                ? state.currentDocument.title
                : $translate.instant('global.menu.help.userGuide');
        }

        function extractDocKeys(results) {
            var seen = {};
            var docKeys = [];
            angular.forEach(results || [], function(result) {
                if (!result || !result.docKey || seen[result.docKey]) {
                    return;
                }
                seen[result.docKey] = true;
                docKeys.push(result.docKey);
            });
            return docKeys;
        }

        function extractChunkIds(results) {
            var chunkIds = [];
            angular.forEach(results || [], function(result) {
                if (result && result.chunkId) {
                    chunkIds.push(result.chunkId);
                }
            });
            return chunkIds;
        }
    }
})();
